import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import "./blogdata.css";

export default function BlogDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`https://nastyauthenticbackend.onrender.com/api/blogs/${slug}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) {
          alert("Blog not found.");
          navigate("/blog");
          return;
        }
        setBlog(data.blog);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Error fetching blog:", err);
        setLoading(false);
      });
  }, [slug, navigate]);

  if (loading)
    return (
      <p
        style={{
          textAlign: "center",
          padding: 80,
          letterSpacing: "0.1em",
          fontSize: 12,
        }}
      >
        <Helmet>
          <title>Loading</title>
        </Helmet>
        LOADING...
      </p>
    );
  if (!blog) return null;

  const { content } = blog;

  return (
    <div style={{ maxWidth: 740, margin: "80px auto", padding: "0 40px 80px" }}>
      {/* Back button */}
      <Helmet>
        {`${blog.title} | Nasty Authentic`}
        <meta name="description" content={blog.meta?.description} />
        <meta name="keywords" content={blog.meta?.focusKeywords?.join(", ")} />
        <link
          rel="canonical"
          href={`https://nastyauthentic.com/blog/${blog.slug}`}
        />

        {/* ── Open Graph (WhatsApp, Facebook, LinkedIn previews) ── */}
        <meta property="og:site_name" content="Nasty Authentic" />
        <meta property="article:published_time" content={blog.createdAt} />
        <meta property="article:tag" content={blog.tags?.join(", ")} />

        {/* ── Twitter Card ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.meta?.description} />
        <meta name="twitter:image" content={blog.image} />

        {/* ── JSON-LD Structured Data (Google rich results) ── */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: blog.title,
            description: blog.meta?.description,
            image: blog.image,
            datePublished: blog.createdAt,
            dateModified: blog.updatedAt,
            author: {
              "@type": "Organization",
              name: "Nasty Authentic",
              url: "https://nastyauthentic.com",
            },
            publisher: {
              "@type": "Organization",
              name: "Nasty Authentic",
              logo: {
                "@type": "ImageObject",
                url: "https://nastyauthentic.com/logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://nastyauthentic.com/blog/${blog.slug}`,
            },
            keywords: blog.meta?.focusKeywords?.join(", "),
          })}
        </script>
      </Helmet>
      <button
        onClick={() => navigate("/blog")}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          letterSpacing: "0.1em",
          fontSize: 11,
          marginBottom: 32,
          padding: 0,
        }}
      >
        ← BACK TO JOURNAL
      </button>

      {/* Hero Image */}
      <img
        src={blog.image}
        alt={blog.title}
        style={{
          width: "100%",
          height: 420,
          objectFit: "cover",
          marginBottom: 32,
        }}
      />

      {/* Tags */}
      <div
        style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}
      >
        {blog.tags?.map((tag) => (
          <span
            key={tag}
            style={{
              fontSize: 9,
              letterSpacing: "0.1em",
              background: "#111",
              color: "#fff",
              padding: "2px 8px",
            }}
          >
            {tag.toUpperCase()}
          </span>
        ))}
      </div>

      {/* Date + Title */}
      <p
        style={{
          fontSize: 11,
          letterSpacing: "0.1em",
          color: "#999",
          marginBottom: 8,
        }}
      >
        {new Date(blog.createdAt).toDateString()}
      </p>
      <h1
        style={{
          fontSize: 40,
          fontWeight: 800,
          letterSpacing: "0.05em",
          marginBottom: 24,
        }}
      >
        {blog.title.toUpperCase()}
      </h1>

      {/* Meta description */}
      <p
        style={{
          fontSize: 14,
          color: "#999",
          marginBottom: 32,
          lineHeight: 1.7,
        }}
      >
        {blog.meta?.description}
      </p>

      <hr
        style={{
          border: "none",
          borderTop: "1px solid #eee",
          marginBottom: 32,
        }}
      />

      {/* Introduction */}
      {content?.introduction && (
        <div style={{ marginBottom: 32 }}>
          <h2
            style={{ fontSize: 20, letterSpacing: "0.15em", marginBottom: 12 }}
          >
            🚀 INTRODUCTION
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: "#999" }}>
            {content.introduction}
          </p>
        </div>
      )}

      {/* Sections */}
      {content?.sections?.map((section, i) => (
        <div
          key={i}
          style={{
            marginBottom: 28,
            paddingLeft: 16,
            borderLeft: "3px solid #111",
          }}
        >
          <h3
            style={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "0.1em",
              marginBottom: 8,
            }}
          >
            {section.emoji} {section.heading.toUpperCase()}
          </h3>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.9,
              color: "#999",
              marginBottom: 8,
            }}
          >
            {section.body}
          </p>
          {section.tip && (
            <p
              style={{
                fontSize: 12,
                color: "#999",
                fontStyle: "italic",
                background: "#f5f5f5",
                padding: "8px 12px",
                letterSpacing: "0.05em",
              }}
            >
              👉 {section.tip}
            </p>
          )}
        </div>
      ))}

      <hr
        style={{
          border: "none",
          borderTop: "1px solid #eee",
          margin: "32px 0",
        }}
      />

      {/* Conclusion */}
      {content?.conclusion && (
        <div style={{ marginBottom: 24 }}>
          <h2
            style={{ fontSize: 15, letterSpacing: "0.15em", marginBottom: 12 }}
          >
            🎯 CONCLUSION
          </h2>
          <p style={{ fontSize: 14, lineHeight: 1.9, color: "#999" }}>
            {content.conclusion}
          </p>
        </div>
      )}

      {/* Call to Action */}
      {content?.callToAction && (
        <div
          style={{
            marginTop: 40,
            padding: "24px 32px",
            background: "#111",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <p
            className="productsmove-button"
            onClick={() => {
              navigate("/products");
            }}
          >
            {content.callToAction}
          </p>
        </div>
      )}
    </div>
  );
}
